import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Calendar } from 'lucide-react'
import { toast } from 'react-toastify'
import Breadcrumb from '../../components/Common/Breadcrumb'
import autoIncrementService from '../../services/autoIncrementService'

const AutoIncrementMasterView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [sequenceData, setSequenceData] = useState(null)
  const [formData, setFormData] = useState({
    sequenceName: '',
    description: '',
    currentValue: 1,
    prefix: '',
    digits: 4,
    incrementBy: 1,
    status: 'Active',
    createdDate: '24-09-2025'
  })

  // Load sequence data from location state or fetch from API
  useEffect(() => {
    if (location.state?.sequenceData) {
      console.log('Received sequence data:', location.state.sequenceData)
      console.log('Sequence ID:', location.state.sequenceData.id)
      console.log('Sequence _id:', location.state.sequenceData._id)
      
      // Ensure we have an id field (either id or _id)
      const sequenceWithId = {
        ...location.state.sequenceData,
        id: location.state.sequenceData.id || location.state.sequenceData._id
      }
      
      setSequenceData(sequenceWithId)
      setFormData({
        sequenceName: sequenceWithId.sequenceName || '',
        description: sequenceWithId.description || '',
        currentValue: sequenceWithId.currentValue || 1,
        prefix: sequenceWithId.prefix || '',
        digits: sequenceWithId.digits || 4,
        incrementBy: sequenceWithId.incrementBy || 1,
        status: sequenceWithId.status || 'Active',
        createdDate: sequenceWithId.createdDate || '24-09-2025'
      })
    } else {
      // If no state data, redirect back to listing
      toast.error('No sequence data found')
      navigate('/settings/auto-increment-master')
    }
  }, [location.state, navigate])

  const handleEdit = () => {
    navigate('/settings/auto-increment-master/edit', { state: { sequenceData } })
  }

  const handleBack = () => {
    navigate('/settings/auto-increment-master')
  }

  const breadcrumbItems = [
    {
      label: 'Settings',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Transactions',
      path: '/settings?tab=transactions',
      onClick: () => navigate('/settings?tab=transactions')
    },
    {
      label: 'Auto Increment Master',
      path: '/settings/auto-increment-master',
      onClick: () => navigate('/settings/auto-increment-master')
    },
    {
      label: 'View',
      path: '/settings/auto-increment-master/view',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Auto Increment Master - View</h1>
          <p>View auto-increment sequence details</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Auto Increment Master - View</h2>
        </div>
          
        <form className="form-content">
          {/* Row 1: Sequence Name, Description, Current Value, Increment By */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="sequenceName" className="form-label required">
                Sequence Name
              </label>
              <input
                type="text"
                id="sequenceName"
                name="sequenceName"
                value={formData.sequenceName}
                className="form-input readonly-input"
                readOnly
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label required">
                Description
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                className="form-input readonly-input"
                readOnly
              />
            </div>

            <div className="form-group">
              <label htmlFor="currentValue" className="form-label required">
                Current Value
              </label>
              <input
                type="number"
                id="currentValue"
                name="currentValue"
                value={formData.currentValue}
                className="form-input readonly-input"
                readOnly
              />
            </div>

            <div className="form-group">
              <label htmlFor="incrementBy" className="form-label required">
                Increment By
              </label>
              <input
                type="number"
                id="incrementBy"
                name="incrementBy"
                value={formData.incrementBy}
                className="form-input readonly-input"
                readOnly
              />
            </div>
          </div>

          {/* Row 2: Prefix, Digits, Status, Created Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="prefix" className="form-label">
                Prefix
              </label>
              <input
                type="text"
                id="prefix"
                name="prefix"
                value={formData.prefix}
                className="form-input readonly-input"
                readOnly
              />
            </div>

            <div className="form-group">
              <label htmlFor="digits" className="form-label">
                Digits
              </label>
              <input
                type="number"
                id="digits"
                name="digits"
                value={formData.digits}
                className="form-input readonly-input"
                readOnly
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label required">
                Status
              </label>
              <input
                type="text"
                id="status"
                name="status"
                value={formData.status}
                className="form-input readonly-input"
                readOnly
              />
            </div>

            <div className="form-group">
              <label htmlFor="createdDate" className="form-label required">
                Created Date
              </label>
              <div className="date-input-wrapper">
                <input
                  type="text"
                  id="createdDate"
                  name="createdDate"
                  value={formData.createdDate}
                  className="form-input readonly-input"
                  readOnly
                />
                <Calendar size={16} className="calendar-icon" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleBack}>
              Back
            </button>
            <button type="button" className="btn btn-primary" onClick={handleEdit}>
              Edit
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AutoIncrementMasterView